import {
  FindNewCompanyGetDto,
  FindNewContactGetDto,
  PositionDto,
} from '../../types/findNew';
import {
  Company,
  LinkedinCompanyCompleteInfo,
  LinkedinProfileExtendedInfo,
  LinkedinWorkExperience,
} from '../../types/crono-extension/linkedin';
import { CompanyScrape } from '../../types/companyScrape';
import { SeniorityLevel } from '../../types/enums/seniorityLevel';
import { calculateDuration } from '../index';

function calculateExperienceStats(workExperiences: LinkedinWorkExperience[]): {
  seniorityLevel: SeniorityLevel | null;
  overallExperienceTime: { years: number; months: number } | null;
} {
  // Mirror the extension logic: use the oldest (last) experience's start date
  const oldestStartDate =
    workExperiences[workExperiences.length - 1]?.timePeriod?.startDate ?? null;
  if (!oldestStartDate)
    return { seniorityLevel: null, overallExperienceTime: null };

  const duration = calculateDuration(oldestStartDate);
  let seniorityLevel: SeniorityLevel;
  if (duration.years >= 8) seniorityLevel = SeniorityLevel.Senior;
  else if (duration.years >= 4) seniorityLevel = SeniorityLevel.Middle;
  else seniorityLevel = SeniorityLevel.Junior;

  return { seniorityLevel, overallExperienceTime: duration };
}

function mapPositionToWorkExperience(
  position: PositionDto,
): LinkedinWorkExperience {
  const hasEnd = position.endDateYear != null;
  const startDate =
    position.startDateYear != null
      ? {
          month: position.startDateMonth ?? 1,
          year: position.startDateYear,
        }
      : null;
  const endDate = hasEnd
    ? {
        month: position.endDateMonth ?? 1,
        year: position.endDateYear!,
      }
    : undefined;
  // The UI shows the duration ("X ye Y mo") from timePeriod.years/months, so
  // compute it from start→end (or now for a current role) instead of leaving 0.
  const duration = startDate
    ? calculateDuration(startDate, endDate)
    : { years: 0, months: 0 };
  return {
    position: position.title ?? '',
    description: position.summary,
    company: {
      name: position.companyName ?? '',
      // LinkedIn company ids are positive; guard against invalid (<= 0) values.
      numericId:
        position.linkedinCompanyId != null && position.linkedinCompanyId > 0
          ? position.linkedinCompanyId.toString()
          : null,
      publicId: null,
    },
    timePeriod: startDate
      ? {
          months: duration.months,
          years: duration.years,
          startDate,
          ...(endDate && { endDate }),
        }
      : undefined,
    // MixRank reports one record per position, so the start date is the role's
    // own and not a company tenure that spans several of them.
    ...(startDate && { positionStartDate: startDate }),
  };
}

/**
 * A MixRank contact record is only useful as a profile when it carries both work
 * experience and a description. MixRank sometimes returns an identity-only stub
 * (name/slug/location populated, but `positions`/`summary` null) even though
 * LinkedIn has the full data — treat those as a miss so callers fall back to
 * LinkedIn instead of rendering an empty profile.
 */
export function isFindNewContactProfileComplete(
  dto: FindNewContactGetDto,
): boolean {
  return !!dto.positions?.length && !!dto.summary?.trim();
}

/**
 * A MixRank company record is only useful when it carries at least a name — the
 * mapper hardcodes `url` to null and passes `name` straight through
 * (can be null), so a name-less DB hit maps to a truthy-but-empty company that
 * would silently replace a richer LinkedIn lookup. Treat those as a miss so
 * callers fall back to LinkedIn.
 */
export function isFindNewCompanyComplete(dto: FindNewCompanyGetDto): boolean {
  return !!dto.name?.trim();
}

export function mapFindNewContactToProfileExtendedInfo(
  dto: FindNewContactGetDto,
): LinkedinProfileExtendedInfo {
  const workExperience = (dto.positions ?? []).map(mapPositionToWorkExperience);

  // The DTO's current-company fields are authoritative for the active company,
  // so prefer `currentCompanyLinkedinNumericId` over the (sometimes missing or
  // invalid) per-position `linkedinCompanyId`. This lets consumers resolve the
  // right company's public id from Crono's internal DB instead of the LinkedIn
  // API. Positions are not ordered current-first, so find the current
  // experience by matching its company id first (most reliable), then by name,
  // then fall back to the first active experience.
  const currentCompanyNumericId =
    dto.currentCompanyLinkedinNumericId?.toString() ?? null;
  if (currentCompanyNumericId != null) {
    const currentName = dto.currentCompanyName?.trim().toLowerCase();
    const activeExperience =
      workExperience.find(
        (w) =>
          !w.timePeriod?.endDate &&
          w.company.numericId === currentCompanyNumericId,
      ) ??
      workExperience.find(
        (w) =>
          !w.timePeriod?.endDate &&
          !!currentName &&
          w.company.name?.trim().toLowerCase() === currentName,
      ) ??
      workExperience.find((w) => !w.timePeriod?.endDate);
    if (activeExperience) {
      activeExperience.company.numericId = currentCompanyNumericId;
    }
  }

  const { seniorityLevel, overallExperienceTime } =
    calculateExperienceStats(workExperience);
  return {
    firstName: dto.firstName,
    lastName: dto.lastName,
    pictureUrl: dto.avatarUrl,
    seniorityLevel: seniorityLevel ?? '',
    overallExperienceTime,
    location: dto.locationName ?? '',
    description: dto.summary,
    // The internal DB has no knowledge of the current user's personal
    // connection to this contact — that's per-user LinkedIn state. Return
    // 'Unknown' rather than wrongly showing "Connect". On LinkedIn the status
    // stays 'Unknown' (DOM connection scraping was removed); SalesNav still
    // resolves it from its own UI/ember data.
    connectionLevel: null,
    connectionStatus: 'Unknown',
    workExperience,
    totalExperiences: workExperience.length,
    currentCompanyNumericId,
    title: dto.currentTitle,
    objectUrn: null,
    profileUrn: null,
    publicId: dto.linkedinSlug,
    profileId: null,
    leadId: null,
    countryCode: dto.countryCode ?? null,
  };
}

export function mapFindNewCompanyToLinkedinCompany(
  dto: FindNewCompanyGetDto,
): LinkedinCompanyCompleteInfo {
  return {
    name: dto.name,
    publicId: dto.linkedinPublicId,
    numberOfEmployees: dto.employeeCount,
    employeeRangeStart: null,
    employeeRangeEnd: null,
    industryId: null,
    industry: dto.industry,
    website: dto.website,
    tagline: dto.companyHeadline,
    description: dto.description,
    url: null,
    numericId: dto.linkedinNumericId?.toString() ?? null,
    phone: null,
    specialities: null,
    geographicArea: dto.region,
    city: null,
    country: dto.country,
    countryCode: dto.countryCode ?? null,
    location: dto.country,
    imageUrl: dto.logoUrl,
    pictureUrl: dto.logoUrl,
  };
}

/**
 * Builds the work-experience company list (`Company[]`) from a FindNew contact's
 * positions. This is the FindNew-first equivalent of LinkedIn's
 * `getUserExperience`, letting callers resolve a person's experience from the
 * internal DB without a live LinkedIn call. The current company is enriched from
 * the contact-level company fields (which FindNew already carries), mirroring the
 * LinkedIn path's `enrichCompany` step for the active role.
 */
export function mapFindNewContactToExperience(
  dto: FindNewContactGetDto,
): Company[] {
  const positions = dto.positions ?? [];
  return positions
    .filter((p) => p.companyName)
    .map((p) => {
      const company: Company = {
        name: p.companyName,
        numericId: p.linkedinCompanyId?.toString() ?? null,
        role: p.title,
        active: p.isCurrent,
      };

      const isCurrentCompany =
        dto.currentCompanyLinkedinNumericId != null &&
        p.linkedinCompanyId === dto.currentCompanyLinkedinNumericId;
      if (isCurrentCompany) {
        if (dto.companyWebsite) {
          company.website = dto.companyWebsite;
        }
        if (dto.companyDescription) {
          company.description = dto.companyDescription;
        }
      }

      return company;
    });
}

/**
 * Builds the current company (`CompanyScrape`) from the company fields embedded
 * in a FindNew contact record. Lets the contact "Load data" flow populate the
 * company columns from the single contact call, with no extra company fetch.
 */
export function mapFindNewContactToCurrentCompany(
  dto: FindNewContactGetDto,
): CompanyScrape {
  const numericId = dto.currentCompanyLinkedinNumericId?.toString() ?? null;
  return {
    id: numericId ? +numericId : 1,
    numericId,
    publicId: null,
    name: dto.currentCompanyName,
    industry: dto.industryName,
    website: dto.companyWebsite,
    description: dto.companyDescription,
    city: dto.companyCity,
    geographicArea: dto.companyRegion,
    country: dto.companyCountry,
    countryCode: dto.companyCountryCode ?? null,
    location: dto.companyCity ?? dto.companyRegion ?? dto.companyCountry,
    expirationDate: new Date(),
  };
}

export function mapFindNewCompanyToCompany(dto: FindNewCompanyGetDto): Company {
  return {
    name: dto.name,
    publicId: dto.linkedinPublicId,
    numericId: dto.linkedinNumericId?.toString() ?? null,
    active: true,
    numberOfEmployees: dto.employeeCount,
    industry: dto.industry,
    website: dto.website,
    description: dto.description,
    imageUrl: dto.logoUrl,
  };
}
